// AddressAutocomplete component with Smarty API integration

import { useState, useMemo, useRef, useEffect } from 'react'
import type { AddressData, AddressSuggestion } from '@/types'
import { debounce } from '@/utils/debounce'
import { logger } from '@/utils/logger'

interface AddressAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onAddressSelect: (address: AddressData) => void
  onManualEntryRequest?: (currentInput: string) => void
  manualEntryActive?: boolean
  placeholder?: string
  id?: string
  name?: string
}

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  onManualEntryRequest,
  manualEntryActive = false,
  placeholder = 'Start typing your address...',
  id = 'address',
  name = 'address',
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [manualPrompt, setManualPrompt] = useState<'none' | 'empty' | 'error'>('none')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Get Smarty key - try multiple sources
  const getSmartyKey = () => {
    // 1. Try window config (injected by Netlify edge function in production)
    const windowKey = (window as unknown as { comparePowerConfig?: { smartyEmbeddedKey?: string } })
      .comparePowerConfig?.smartyEmbeddedKey

    if (windowKey && windowKey !== '{{SMARTY_KEY}}') {
      return windowKey
    }

    // 2. Try Vite environment variable (for local development)
    const viteKey = import.meta.env.VITE_SMARTY_EMBEDDED_KEY
    if (viteKey) {
      return viteKey
    }

    return ''
  }

  // Fetch suggestions from Smarty API (debounced)
  const fetchSuggestions = useMemo(
    () =>
      debounce(async (input: string, selected?: string) => {
        if (!input || input.length < 3) {
          setSuggestions([])
          setIsOpen(false)
          setManualPrompt('none')
          return
        }

        const apiKey = getSmartyKey()
        if (!apiKey || apiKey === '{{SMARTY_KEY}}') {
          logger.warn({ area: 'address' }, 'Smarty API key not configured')
          setSuggestions([])
          setIsOpen(false)
          setManualPrompt('error')
          return
        }

        setIsLoading(true)
        setManualPrompt('none')

        try {
          const params = new URLSearchParams({
            key: apiKey,
            search: input,
            max_results: '10',
            source: 'all',
          })

          if (selected) {
            params.append('selected', selected)
          }

          const response = await fetch(
            `https://us-autocomplete-pro.api.smarty.com/lookup?${params}`
          )

          if (!response.ok) {
            throw new Error(`Smarty API error: ${response.status}`)
          }

          const data = await response.json()
          const nextSuggestions = data.suggestions || []
          setSuggestions(nextSuggestions)
          setIsOpen(nextSuggestions.length > 0)
          setManualPrompt(nextSuggestions.length === 0 ? 'empty' : 'none')
          setSelectedIndex(-1)
        } catch (error) {
          logger.error({ area: 'address' }, 'Address autocomplete error', error instanceof Error ? error : undefined)
          setSuggestions([])
          setIsOpen(false)
          setManualPrompt('error')
        } finally {
          setIsLoading(false)
        }
      }, 300),
    []
  )

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    onChange(newValue)
    setManualPrompt('none')
    fetchSuggestions(newValue)
  }

  // Parse suggestion into AddressData
  const parseSuggestion = (suggestion: AddressSuggestion): AddressData => {
    const streetParts = suggestion.street_line.split(' ')
    const streetNumber = streetParts[0] || ''
    const route = streetParts.slice(1).join(' ') || suggestion.street_line

    const fullAddress = suggestion.secondary
      ? `${suggestion.street_line} ${suggestion.secondary}, ${suggestion.city}, ${suggestion.state} ${suggestion.zipcode}`
      : `${suggestion.street_line}, ${suggestion.city}, ${suggestion.state} ${suggestion.zipcode}`

    return {
      street: suggestion.street_line,
      streetNumber,
      route,
      unit: suggestion.secondary || undefined,
      city: suggestion.city,
      state: suggestion.state,
      stateCode: suggestion.state,
      zip: suggestion.zipcode,
      country: 'US',
      fullAddress,
    }
  }

  // Handle suggestion selection
  const handleSelect = (suggestion: AddressSuggestion) => {
    // If this suggestion has multiple entries (apartment building), expand it
    if (suggestion.entries && suggestion.entries > 1) {
      const selected = `${suggestion.street_line} (${suggestion.entries}) ${suggestion.city} ${suggestion.state} ${suggestion.zipcode}`
      onChange(suggestion.street_line)
      fetchSuggestions(suggestion.street_line, selected)
      return
    }

    // Single address - select it
    const parsed = parseSuggestion(suggestion)
    onChange(parsed.fullAddress)
    onAddressSelect(parsed)
    setSuggestions([])
    setIsOpen(false)
    setManualPrompt('none')
  }

  const handleManualEntryRequest = () => {
    setSuggestions([])
    setIsOpen(false)
    setManualPrompt('none')
    onManualEntryRequest?.(value)
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setSelectedIndex(-1)
        break
    }
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Format suggestion display
  const formatSuggestion = (suggestion: AddressSuggestion): string => {
    let text = suggestion.street_line
    if (suggestion.secondary) {
      text += ` ${suggestion.secondary}`
    }
    if (suggestion.entries && suggestion.entries > 1) {
      text += ` (${suggestion.entries} entries)`
    }
    text += `, ${suggestion.city}, ${suggestion.state} ${suggestion.zipcode}`
    return text
  }

  return (
    <div ref={containerRef} className="autocomplete-container">
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls="address-suggestions"
      />

      {isLoading && (
        <div className="autocomplete-loading">
          <span className="spinner spinner--small"></span>
        </div>
      )}

      {isOpen && suggestions.length > 0 && (
        <div
          id="address-suggestions"
          className="autocomplete-dropdown show"
          role="listbox"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.street_line}-${suggestion.zipcode}-${index}`}
              className={`autocomplete-item ${
                index === selectedIndex ? 'autocomplete-item--selected' : ''
              }`}
              role="option"
              aria-selected={index === selectedIndex}
              onClick={() => handleSelect(suggestion)}
            >
              {formatSuggestion(suggestion)}
            </div>
          ))}
        </div>
      )}

      {manualPrompt !== 'none' && !manualEntryActive && onManualEntryRequest && (
        <div className="autocomplete-manual" role="status">
          <span>
            {manualPrompt === 'empty'
              ? 'No matching address found.'
              : 'Address lookup is unavailable right now.'}
          </span>
          <button
            type="button"
            className="autocomplete-manual__button"
            onClick={handleManualEntryRequest}
          >
            Enter address manually
          </button>
        </div>
      )}
    </div>
  )
}
